import React, { useState } from 'react'
import { ScrollView, Text, View } from 'react-native'
import { useMutation, useQuery } from '@apollo/client'
import { cva } from 'class-variance-authority'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { SafeAreaView } from 'react-native-safe-area-context'

import { semantic } from '@/constants/colors'
import { UserFlags } from '@/constants/common'
import { RESET_USER_KYC_LV1 } from '@/constants/graphql/mutations'
import { GET_USER } from '@/constants/graphql/queries'
import { cn } from '@/utils/cn'
import { useRouter } from '@/utils/navigation'
import { maxKYC1Flag } from '@/utils/user'
import { AuthContext } from '@/stores/auth'
import { Button, useToast } from '@/components/base'
import { Cancel, Reviewing, Spinner, Verified } from '@/components/icon'
import { Stack } from '@/components/layout'
import NavigationBar from '@/components/NavigationBar'

import KYCSubmittingScreen from './submit'

type Status = 'verifying' | 'verified' | 'rejected' | 'submitting' | 'unknown'
type KycResultStatus = Extract<Status, 'verifying' | 'verified' | 'rejected'>

/**
 * Represents the KYC (Know Your Customer) screen.
 * This screen is responsible for checking if the user has completed KYC and showing the KYC form if not.
 */
export default function KYCScreen() {
  const { t } = useTranslation()
  const router = useRouter()
  const [status, setStatus] = useState<Status>('unknown')
  const user = AuthContext.useSelector((state) => state.context.values?.user)
  const toast = useToast()
  // Check if user has completed KYC
  const {
    data: userData,
    loading,
    refetch
  } = useQuery(GET_USER, {
    variables: {
      id: user?.id
    },
    onCompleted: (data) => {
      // console.log(data)
      const userFlags = data.user?.flags
      const maxFlag = userFlags ? maxKYC1Flag(userFlags) : UserFlags.EMPTY

      // HACK TODO
      switch (maxFlag) {
        case UserFlags.KYC_1_REJECTED:
          setStatus('rejected')
          break
        case UserFlags.KYC_1_SUBMITTED:
          setStatus('verifying')
          break
        case UserFlags.KYC_1_VERIFIED:
          setStatus('verified')
          break
        default:
          setStatus('submitting')
          break
      }
    },
    onError: (error) => {
      console.log(error)
      toast.show({ variant: 'warning', description: t('kyc:dataLoadFailed') })
    }
  })

  const [resetKYC] = useMutation(RESET_USER_KYC_LV1, {
    onCompleted: () => {
      setStatus('submitting')
      refetch()
    },
    onError: (error) => {
      console.log(error)
      toast.show({ variant: 'error', description: t('kyc:resetFailed') })
    }
  })

  function handleConfirm() {
    if (status === 'verifying' || status === 'verified') {
      router.back()
    } else if (status === 'rejected') {
      resetKYC()
    }
  }

  function setSuccess() {
    setStatus('verifying')
  }

  if (loading || status === 'unknown') {
    return (
      <>
        <Stack.Screen options={{ header: () => <NavigationBar>{t('kyc:identityVerification')}</NavigationBar> }} />
        <View className={'flex-1 items-center justify-center bg-background-primary'}>
          <View className={'mb-3 size-6 animate-spin items-center justify-center'}>
            <Spinner className={'size-6 text-primary'} />
          </View>
        </View>
      </>
    )
  }

  if (status === 'verifying' || status === 'verified' || status === 'rejected') {
    return (
      <>
        <Stack.Screen options={{ header: () => <NavigationBar>{t('kyc:identityVerification')}</NavigationBar> }} />
        <SafeAreaView className={'flex-1 bg-background-primary'}>
          <ScrollView>
            <KYCResultScreen status={status} handleConfirm={handleConfirm} reason={userData?.user.rejectKyc1Reason} />
          </ScrollView>
        </SafeAreaView>
      </>
    )
  } else {
    return <KYCSubmittingScreen setSuccess={setSuccess} />
  }
}

function getState(t: TFunction) {
  return {
  verifying: {
    title: t('kyc:reviewPending'),
    text: t('kyc:reviewPendingDescription'),
    icon: Reviewing,
    color: semantic.text.quaternary,
    buttonText: t('ui:gotIt')
  },
  verified: {
    title: t('kyc:reviewApproved'),
    text: t('kyc:reviewApprovedDescription'),
    icon: Verified,
    color: semantic.text.success,
    buttonText: t('ui:gotIt')
  },
  rejected: {
    title: t('kyc:reviewRejected'),
    text: t('kyc:reviewRejectedDescription'),
    icon: Cancel,
    color: semantic.text.danger,
    buttonText: t('kyc:fillInAgain')
  }
  }
}

const iconVariants = cva('size-20', {
  variants: {
    status: {
      verifying: 'text-text-quaternary',
      verified: 'text-success',
      rejected: 'text-danger'
    }
  },
  defaultVariants: {
    status: 'verifying'
  }
})

function KYCResultScreen({
  status,
  reason = '',
  handleConfirm
}: {
  status: KycResultStatus
  reason: string
  handleConfirm: () => void
}) {
  const { t } = useTranslation()
  const state = getState(t)[status]
  const Icon = state.icon

  // TODO: Add a button for customer service?
  return (
    <View className={'top-[197px] flex-1 bg-background-primary'}>
      <View className={'p-4'}>
        <View className={'items-center gap-3'}>
          <Icon className={cn(iconVariants({ status }))} />
          <Text className={'text-2xl font-semibold text-text-primary'}>{state.title}</Text>
          <View>
            <Text className={'text-center text-sm text-text-secondary'}>{state.text}</Text>
            {status === 'rejected' && <Text className={'text-center text-sm text-text-secondary'}>{reason}</Text>}
          </View>
        </View>
        <View className={'size-6'} />
        <Button intent={'primary'} variant={'solid'} size={'md'} onPress={handleConfirm}>
          {state.buttonText}
        </Button>
      </View>
    </View>
  )
}
